import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { request } from '../lib/api.ts';
import type { Campaign } from './campaigns/types.ts';

interface CampaignsResponse {
  campaigns: Campaign[];
}

export function OverviewPage(): React.ReactElement {
  const { t } = useTranslation();

  const campaignsQuery = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => request<CampaignsResponse>('/campaigns'),
  });

  const campaigns = campaignsQuery.data?.campaigns ?? [];
  const activeCount = campaigns.filter((c) => c.status === 'active').length;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t('overview.title')}</h1>
          <div className="sub">{t('overview.subtitle')}</div>
        </div>
      </div>

      <div className="metric-row">
        <div className="metric">
          <div className="label">⚡ {t('overview.metricActiveCampaigns')}</div>
          <div className="value">{activeCount}</div>
          <div className="delta">{t('overview.metricActiveCampaignsHint', { total: campaigns.length })}</div>
        </div>
        <div className="metric amber">
          <div className="label">⏳ {t('overview.metricPending')}</div>
          <div className="value">0</div>
          <div className="delta">{t('overview.metricPendingHint')}</div>
        </div>
        <div className="metric cool">
          <div className="label">📤 {t('overview.metricPublished')}</div>
          <div className="value">0</div>
          <div className="delta">{t('overview.metricPublishedHint')}</div>
        </div>
        <div className="metric hot">
          <div className="label">👀 {t('overview.metricViews')}</div>
          <div className="value">0</div>
          <div className="delta">{t('overview.metricViewsHint')}</div>
        </div>
      </div>

      <div className="section" style={{ marginTop: 16 }}>
        <div className="section-head">
          <h2>
            {t('overview.recentCampaigns')} <span className="count">{campaigns.length}</span>
          </h2>
        </div>
        <div className="section-body" style={{ padding: 0 }}>
          {campaignsQuery.isLoading && <div style={{ padding: 16 }}>{t('common.loading')}</div>}
          {campaigns.length === 0 && !campaignsQuery.isLoading && (
            <div style={{ padding: 24, color: 'var(--text-3)' }}>
              {t('overview.noCampaigns')}
            </div>
          )}
          {campaigns.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>{t('overview.tableCampaign')}</th>
                  <th>{t('overview.tableVertical')}</th>
                  <th>{t('overview.tableKeywords')}</th>
                  <th>{t('overview.tableStatus')}</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.slice(0, 5).map((c) => (
                  <tr key={c.id}>
                    <td>
                      <div className="name">{c.name}</div>
                      <div className="desc">{t('overview.cron')}: {c.cronSchedule}</div>
                    </td>
                    <td>{c.vertical}</td>
                    <td>{c.keywords.join(', ') || '—'}</td>
                    <td>
                      <span className={`pill ${c.status === 'active' ? 'live' : ''}`}>{c.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}
